import { http } from '../../services/http';
import type { FeatureState } from '../auth/types';

export interface TenantProfile {
  id: string;
  slug: string;
  name: string;
  legalName: string | null;
  businessCategory: string;
  status: string;
  contactEmail: string | null;
  contactPhone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string;
  onboardingStep: string;
}

export interface Branding {
  displayName: string;
  primaryColor: string;
  secondaryColor: string | null;
  accentColor: string | null;
  emailSenderName: string | null;
  emailFooter: string | null;
  logo: { id: string; url: string | null } | null;
  favicon: { id: string; url: string | null } | null;
  version: number;
}

export type WorkingHours = Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', [string, string][]>;

export interface Settings {
  timezone: string;
  currency: string;
  locale: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  weekStart: number;
  workingHours?: WorkingHours;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  transferRequiresApproval?: boolean;
  /** Days without activity before an open lead counts as stale (OD-24). */
  staleLeadDays?: number;
  /** Minutes before a follow-up that its reminder goes out. */
  defaultReminderOffsetMin?: number;
  /** Effective outcome codes per activity type (Phase 6). */
  outcomeCodes?: OutcomeCodes;
  /** True when the business replaced the platform defaults. */
  outcomeCodesCustom?: boolean;
  version?: number;
}

export type OutcomeActivity = 'call' | 'whatsapp' | 'email' | 'meeting' | 'site_visit';
export type OutcomeCodes = Record<OutcomeActivity, { code: string; label: string }[]>;

export interface Onboarding {
  currentStep: string;
  completed: boolean;
  steps: { key: string; done: boolean }[];
}

export const tenantApi = {
  profile: () => http.get<TenantProfile>('/tenant'),
  updateProfile: (body: Partial<Omit<TenantProfile, 'id' | 'slug' | 'status' | 'onboardingStep'>>) =>
    http.patch<TenantProfile>('/tenant/profile', body),
  branding: () => http.get<Branding>('/tenant/branding'),
  updateBranding: (body: Record<string, unknown>) => http.patch<Branding>('/tenant/branding', body),
  settings: () => http.get<Settings>('/tenant/settings'),
  updateSettings: (body: Record<string, unknown> & { expectedVersion: number }) =>
    http.patch<Settings>('/tenant/settings', body),
  onboarding: () => http.get<Onboarding>('/tenant/onboarding'),
  completeStep: (step: string) => http.post<Onboarding>(`/tenant/onboarding/${step}/complete`),
  entitlements: () => http.get<Record<string, FeatureState>>('/tenant/entitlements'),
};

export const tenantKeys = {
  profile: ['tenant', 'profile'] as const,
  branding: ['tenant', 'branding'] as const,
  settings: ['tenant', 'settings'] as const,
  onboarding: ['tenant', 'onboarding'] as const,
  entitlements: ['tenant', 'entitlements'] as const,
};

/** Signs, uploads directly to the media provider, then asks the API to verify the stored file. */
export async function uploadImage(
  file: File,
  purpose: 'tenant_logo' | 'tenant_favicon',
): Promise<{ id: string; url: string | null }> {
  const signed = await http.post<{ mediaId: string; uploadUrl: string; fields: Record<string, string> }>(
    '/media/upload-signatures',
    { purpose, fileName: file.name, mimeType: file.type, bytes: file.size },
  );
  const form = new FormData();
  for (const [key, value] of Object.entries(signed.fields)) form.append(key, value);
  form.append('file', file);
  const upload = await fetch(signed.uploadUrl, { method: 'POST', body: form });
  if (!upload.ok) throw new Error('The upload was rejected by the media service.');
  return http.post<{ id: string; url: string | null }>(`/media/${signed.mediaId}/finalize`);
}
